import React from 'react';

import {
  StepHeaderState,
  StyledStepHeader,
  StepHeaderContent,
  StepHeaderTitle,
  StepHeaderSubtitle,
  StepHeaderTrailing,
  StepHeaderCounter,
  ArrowButton,
} from './styles';

export type { StepHeaderState } from './styles';

/**
 * StepHeader
 *
 * Card-cabeçalho de uma etapa/seção do fluxo de pedido de abastecimento.
 * NÃO é um stepper: cada etapa renderiza o seu próprio StepHeader.
 *
 * Anatomia (esquerda → direita):
 * - título + subtítulo opcional
 * - slot direito: contador de texto ("Local 1 de 2"), botão-seta, ou nada
 *
 * Não há barra de acento lateral — a ativação é indicada apenas pela seta
 * (cinza em repouso, azul quando `arrowActive`).
 *
 * Valores tipográficos seguem TEXTOS_ABASTECIMENTO.md (Figma):
 * - state="inactive": título 16px #3D3D3D (card seletor "Local de abastecimento")
 * - state="active"/"completed": título 22px #0B29FB (seção em preenchimento / resumo)
 *
 * @example Seletor de local (título cinza), seta cinza em repouso
 * <StepHeader
 *   title="Local de abastecimento"
 *   description="Selecione um ou mais locais que precisam de abastecimento"
 *   state="inactive"
 *   onArrowClick={goToNext}
 * />
 *
 * @example Mesmo card com local já selecionado (seta azul)
 * <StepHeader title="Local de abastecimento" description="..."
 *   state="inactive" onArrowClick={goToNext} arrowActive />
 *
 * @example Seção ativa com contador (título azul 22px)
 * <StepHeader title="Refeitório fundos" description="preencha os dados abaixo"
 *   state="active" counter="Local 1 de 2" />
 */
export type StepHeaderProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> & {
  /** Texto do título principal */
  title: string;

  /** Subtítulo opcional abaixo do título */
  description?: string;

  /**
   * Estado da etapa — controla o tratamento do título.
   * 'inactive' → 16px #3D3D3D (seletor); 'active'/'completed' → 22px #0B29FB.
   * @default 'inactive'
   */
  state?: StepHeaderState;

  /**
   * Texto do contador à direita (ex.: "Local 1 de 2"). Tem precedência sobre
   * a seta quando ambos são fornecidos.
   */
  counter?: string;

  /**
   * Handler de clique do botão-seta. Quando definido (e sem `counter`),
   * o botão-seta é renderizado.
   */
  onArrowClick?: () => void;

  /**
   * Deixa a seta no estado ativo (azul). Desacoplado de `state`, pois o card
   * seletor mantém o título cinza mesmo com a seta ativa.
   * @default false
   */
  arrowActive?: boolean;

  /**
   * Label acessível do botão-seta
   * @default 'Avançar etapa'
   */
  arrowLabel?: string;

  /** Desabilita o botão-seta */
  arrowDisabled?: boolean;
};

const ArrowRightIcon = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const StepHeader = ({
  title,
  description,
  state = 'inactive',
  counter,
  onArrowClick,
  arrowActive = false,
  arrowLabel = 'Avançar etapa',
  arrowDisabled = false,
  ...rest
}: StepHeaderProps): React.ReactElement => {
  const renderTrailing = (): React.ReactNode => {
    if (counter) {
      return <StepHeaderCounter>{counter}</StepHeaderCounter>;
    }

    if (onArrowClick) {
      return (
        <ArrowButton
          type="button"
          $active={arrowActive}
          onClick={onArrowClick}
          disabled={arrowDisabled}
          aria-label={arrowLabel}
        >
          <ArrowRightIcon />
        </ArrowButton>
      );
    }

    return null;
  };

  const trailing = renderTrailing();

  return (
    <StyledStepHeader {...rest}>
      <StepHeaderContent>
        <StepHeaderTitle $state={state}>{title}</StepHeaderTitle>
        {description && (
          <StepHeaderSubtitle>{description}</StepHeaderSubtitle>
        )}
      </StepHeaderContent>

      {trailing && <StepHeaderTrailing>{trailing}</StepHeaderTrailing>}
    </StyledStepHeader>
  );
};

export default StepHeader;
